import { useState, useEffect, useCallback } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import type { Company, CompanyDetail as _CompanyDetail } from '../types'

const API_BASE = '/api/v1'

// Types for progress tracking
interface ResearchProgress {
  run_id: number
  status: string
  progress_pct: number
  message: string
  tasks_completed: number
  tasks_total: number
  searches_completed: number
  error?: string
  done?: boolean
}

// API Functions
async function fetchCompanies(): Promise<Company[]> {
  const res = await fetch(`${API_BASE}/company-research/companies`)
  if (!res.ok) throw new Error('Failed to fetch companies')
  return res.json()
}

async function createCompany(data: {
  ticker: string
  company_name: string
  sector?: string
  industry?: string
}): Promise<Company> {
  const res = await fetch(`${API_BASE}/company-research/companies`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  })
  if (!res.ok) {
    const err = await res.json()
    throw new Error(err.detail || 'Failed to create company')
  }
  return res.json()
}

async function triggerResearch(companyId: number): Promise<{ id: number }> {
  const res = await fetch(`${API_BASE}/company-research/companies/${companyId}/research`, {
    method: 'POST',
  })
  if (!res.ok) throw new Error('Failed to trigger research')
  return res.json()
}

async function fetchProgress(runId: number): Promise<ResearchProgress> {
  const res = await fetch(`${API_BASE}/company-research/runs/${runId}/progress`)
  if (!res.ok) throw new Error('Failed to fetch progress')
  return res.json()
}

// Signal type colors - exported for use in other components
export const signalColors: Record<string, string> = {
  hiring: 'bg-blue-500/20 text-blue-400',
  product_launch: 'bg-green-500/20 text-green-400',
  partnership: 'bg-purple-500/20 text-purple-400',
  acquisition: 'bg-orange-500/20 text-orange-400',
  risk_event: 'bg-red-500/20 text-red-400',
  catalyst: 'bg-yellow-500/20 text-yellow-400',
  earnings: 'bg-cyan-500/20 text-cyan-400',
  competitor_move: 'bg-pink-500/20 text-pink-400',
  executive_change: 'bg-indigo-500/20 text-indigo-400',
  regulatory: 'bg-amber-500/20 text-amber-400',
  guidance: 'bg-teal-500/20 text-teal-400',
  news: 'bg-gray-500/20 text-gray-400',
}

// Format relative time
function formatRelativeTime(dateStr?: string): string {
  if (!dateStr) return 'Never'
  const date = new Date(dateStr)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60))
  const diffDays = Math.floor(diffHours / 24)

  if (diffHours < 1) return 'Just now'
  if (diffHours < 24) return `${diffHours}h ago`
  if (diffDays < 7) return `${diffDays}d ago`
  return date.toLocaleDateString()
}

// Progress Bar Component
function ProgressBar({ progress, message }: { progress: number; message: string }) {
  return (
    <div className="mt-2 sm:mt-3">
      <div className="flex justify-between text-[10px] sm:text-xs text-star-dim mb-1">
        <span className="truncate mr-2">{message}</span>
        <span className="shrink-0">{progress}%</span>
      </div>
      <div className="h-1.5 sm:h-2 bg-void-200/30 rounded-full overflow-hidden">
        <motion.div
          className="h-full bg-signal-blue"
          initial={{ width: 0 }}
          animate={{ width: `${progress}%` }}
          transition={{ duration: 0.3 }}
        />
      </div>
    </div>
  )
}

// Company Card Component
function CompanyCard({
  company,
  onResearch,
  activeRunId,
  progress
}: {
  company: Company
  onResearch: (id: number) => void
  activeRunId?: number
  progress?: ResearchProgress
}) {
  const isStale = !company.last_research_at ||
    (new Date().getTime() - new Date(company.last_research_at).getTime()) > 7 * 24 * 60 * 60 * 1000
  const isResearching = activeRunId !== undefined && progress && progress.status !== 'completed' && progress.status !== 'failed'

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className={`glass-card p-3 sm:p-4 rounded-lg border ${isResearching ? 'border-signal-blue/50' : 'border-void-200/20'}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <Link
            to={`/research/${company.id}`}
            className="text-base sm:text-lg font-semibold text-star-bright hover:text-signal-blue transition-colors"
          >
            {company.ticker}
          </Link>
          <p className="text-xs sm:text-sm text-star-dim truncate">{company.company_name}</p>
        </div>
        <button
          onClick={() => onResearch(company.id)}
          disabled={isResearching}
          className={`px-3 py-1.5 min-h-[36px] rounded text-xs sm:text-sm font-medium transition-colors shrink-0 ${
            isResearching
              ? 'bg-signal-blue/10 text-signal-blue cursor-not-allowed'
              : isStale
              ? 'bg-signal-blue/20 text-signal-blue hover:bg-signal-blue/30'
              : 'bg-void-200/20 text-star-dim hover:bg-void-200/30'
          }`}
        >
          {isResearching ? (
            <span className="flex items-center gap-1">
              <span className="animate-spin">⟳</span>
              <span>Running</span>
            </span>
          ) : 'Research'}
        </button>
      </div>

      {/* Progress indicator when researching */}
      {isResearching && progress && (
        <ProgressBar progress={progress.progress_pct} message={progress.message} />
      )}

      {/* Show error if failed */}
      {progress?.status === 'failed' && (
        <div className="mt-2 sm:mt-3 text-[10px] sm:text-xs text-signal-red bg-signal-red/10 px-2 py-1 rounded">
          Failed: {progress.error || 'Unknown error'}
        </div>
      )}

      {/* Normal content when not researching */}
      {!isResearching && (
        <>
          <div className="mt-2 sm:mt-3 flex flex-wrap items-center gap-2 text-[10px] sm:text-xs text-star-dim">
            {company.sector && (
              <span className="px-1.5 sm:px-2 py-0.5 rounded bg-void-200/20">{company.sector}</span>
            )}
            {company.industry && (
              <span className="px-1.5 sm:px-2 py-0.5 rounded bg-void-200/10">{company.industry}</span>
            )}
          </div>

          <div className="mt-2 sm:mt-3 flex items-center justify-between text-[10px] sm:text-xs">
            <span className={isStale ? 'text-signal-red' : 'text-star-dim'}>
              Last: {formatRelativeTime(company.last_research_at)}
            </span>
            {company.market_cap_usd && (
              <span className="text-star-dim">
                ${(company.market_cap_usd / 1e9).toFixed(1)}B
              </span>
            )}
          </div>
        </>
      )}
    </motion.div>
  )
}

// Add Company Modal
function AddCompanyModal({
  isOpen,
  onClose,
  onSubmit
}: {
  isOpen: boolean
  onClose: () => void
  onSubmit: (data: { ticker: string; company_name: string; sector?: string; industry?: string }) => void
}) {
  const [formData, setFormData] = useState({
    ticker: '',
    company_name: '',
    sector: '',
    industry: '',
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    onSubmit({
      ticker: formData.ticker,
      company_name: formData.company_name,
      sector: formData.sector || undefined,
      industry: formData.industry || undefined,
    })
    setFormData({ ticker: '', company_name: '', sector: '', industry: '' })
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="glass-card p-4 sm:p-6 rounded-lg w-full max-w-md max-h-[90vh] overflow-y-auto"
      >
        <h2 className="text-lg sm:text-xl font-semibold text-star-bright mb-4">Add Company</h2>

        <form onSubmit={handleSubmit} className="space-y-3 sm:space-y-4">
          <div>
            <label className="block text-xs sm:text-sm text-star-dim mb-1">Ticker Symbol *</label>
            <input
              type="text"
              value={formData.ticker}
              onChange={(e) => setFormData({ ...formData, ticker: e.target.value.toUpperCase() })}
              className="w-full px-3 py-2 min-h-[44px] bg-void-100 border border-void-200/30 rounded text-star-bright focus:outline-none focus:border-signal-blue text-sm sm:text-base"
              placeholder="NVDA"
              required
            />
          </div>

          <div>
            <label className="block text-xs sm:text-sm text-star-dim mb-1">Company Name *</label>
            <input
              type="text"
              value={formData.company_name}
              onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
              className="w-full px-3 py-2 min-h-[44px] bg-void-100 border border-void-200/30 rounded text-star-bright focus:outline-none focus:border-signal-blue text-sm sm:text-base"
              placeholder="NVIDIA Corporation"
              required
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
            <div>
              <label className="block text-xs sm:text-sm text-star-dim mb-1">Sector</label>
              <input
                type="text"
                value={formData.sector}
                onChange={(e) => setFormData({ ...formData, sector: e.target.value })}
                className="w-full px-3 py-2 min-h-[44px] bg-void-100 border border-void-200/30 rounded text-star-bright focus:outline-none focus:border-signal-blue text-sm sm:text-base"
                placeholder="Technology"
              />
            </div>
            <div>
              <label className="block text-xs sm:text-sm text-star-dim mb-1">Industry</label>
              <input
                type="text"
                value={formData.industry}
                onChange={(e) => setFormData({ ...formData, industry: e.target.value })}
                className="w-full px-3 py-2 min-h-[44px] bg-void-100 border border-void-200/30 rounded text-star-bright focus:outline-none focus:border-signal-blue text-sm sm:text-base"
                placeholder="Semiconductors"
              />
            </div>
          </div>

          <div className="flex justify-end gap-3 mt-4 sm:mt-6">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 min-h-[44px] text-star-dim hover:text-star-bright transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 min-h-[44px] bg-signal-blue text-white rounded hover:bg-signal-blue/80 transition-colors"
            >
              Add Company
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  )
}

// Main Component
export function CompanyResearch() {
  const [showAddModal, setShowAddModal] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  // Track active research: { companyId: runId }
  const [activeResearch, setActiveResearch] = useState<Record<number, number>>({})
  // Track progress for each run
  const [progressMap, setProgressMap] = useState<Record<number, ResearchProgress>>({})
  const queryClient = useQueryClient()

  const { data: companies = [], isLoading, error } = useQuery({
    queryKey: ['companies'],
    queryFn: fetchCompanies,
  })

  const createMutation = useMutation({
    mutationFn: createCompany,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['companies'] })
      setShowAddModal(false)
    },
  })

  // Poll for progress of active research runs
  const pollProgress = useCallback(async (companyId: number, runId: number) => {
    try {
      const progress = await fetchProgress(runId)
      setProgressMap(prev => ({ ...prev, [companyId]: progress }))

      // If completed or failed, stop polling and refresh companies
      if (progress.status === 'completed' || progress.status === 'failed') {
        setActiveResearch(prev => {
          const next = { ...prev }
          delete next[companyId]
          return next
        })
        queryClient.invalidateQueries({ queryKey: ['companies'] })

        // Clear progress after a delay so user can see final state
        if (progress.status === 'completed') {
          setTimeout(() => {
            setProgressMap(prev => {
              const next = { ...prev }
              delete next[companyId]
              return next
            })
          }, 3000)
        }
      }
    } catch (err) {
      console.error('Failed to poll progress:', err)
    }
  }, [queryClient])

  // Set up polling interval for active research
  useEffect(() => {
    const activeEntries = Object.entries(activeResearch)
    if (activeEntries.length === 0) return

    const intervalId = setInterval(() => {
      activeEntries.forEach(([companyIdStr, runId]) => {
        pollProgress(parseInt(companyIdStr), runId)
      })
    }, 2000) // Poll every 2 seconds

    // Initial poll
    activeEntries.forEach(([companyIdStr, runId]) => {
      pollProgress(parseInt(companyIdStr), runId)
    })

    return () => clearInterval(intervalId)
  }, [activeResearch, pollProgress])

  const researchMutation = useMutation({
    mutationFn: triggerResearch,
    onSuccess: (data, companyId) => {
      // Track this research run
      setActiveResearch(prev => ({ ...prev, [companyId]: data.id }))
      // Initialize progress
      setProgressMap(prev => ({
        ...prev,
        [companyId]: {
          run_id: data.id,
          status: 'pending',
          progress_pct: 0,
          message: 'Starting research...',
          tasks_completed: 0,
          tasks_total: 7,
          searches_completed: 0,
        }
      }))
    },
  })

  // Filter companies by search
  const filteredCompanies = companies.filter(
    (c) =>
      c.ticker.toLowerCase().includes(searchQuery.toLowerCase()) ||
      c.company_name.toLowerCase().includes(searchQuery.toLowerCase())
  )

  // Group by sector
  const companiesBySector = filteredCompanies.reduce((acc, company) => {
    const sector = company.sector || 'Other'
    if (!acc[sector]) acc[sector] = []
    acc[sector].push(company)
    return acc
  }, {} as Record<string, Company[]>)

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-2 border-signal-blue border-t-transparent rounded-full" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="glass-card p-6 rounded-lg border border-signal-red/30">
        <h2 className="text-lg font-semibold text-signal-red mb-2">Error</h2>
        <p className="text-star-dim">{(error as Error).message}</p>
      </div>
    )
  }

  return (
    <div className="max-w-7xl mx-auto px-3 sm:px-4 py-4 sm:py-6 space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-star-bright">Company Research</h1>
          <p className="text-star-dim text-xs sm:text-sm mt-1">Deep research on companies using Exa AI</p>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="px-4 py-2 min-h-[44px] bg-signal-blue text-white rounded-lg hover:bg-signal-blue/80 transition-colors flex items-center justify-center gap-2 self-start sm:self-auto"
        >
          <span>+</span>
          <span>Add Company</span>
        </button>
      </div>

      {/* Search */}
      <div className="relative">
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search companies..."
          className="w-full px-3 sm:px-4 py-3 min-h-[44px] bg-void-100 border border-void-200/30 rounded-lg text-star-bright placeholder-star-dim/50 focus:outline-none focus:border-signal-blue text-sm sm:text-base"
        />
      </div>

      {/* Stats bar */}
      <div className="grid grid-cols-3 gap-2 sm:gap-4">
        <div className="glass-card p-2 sm:p-4 rounded-lg text-center">
          <div className="text-lg sm:text-2xl font-bold text-star-bright">{companies.length}</div>
          <div className="text-[10px] sm:text-sm text-star-dim">Companies</div>
        </div>
        <div className="glass-card p-2 sm:p-4 rounded-lg text-center">
          <div className="text-lg sm:text-2xl font-bold text-star-bright">
            {companies.filter(c => c.last_research_at).length}
          </div>
          <div className="text-[10px] sm:text-sm text-star-dim">Researched</div>
        </div>
        <div className="glass-card p-2 sm:p-4 rounded-lg text-center">
          <div className="text-lg sm:text-2xl font-bold text-signal-red">
            {companies.filter(c => !c.last_research_at ||
              (new Date().getTime() - new Date(c.last_research_at).getTime()) > 7 * 24 * 60 * 60 * 1000
            ).length}
          </div>
          <div className="text-[10px] sm:text-sm text-star-dim">Need Refresh</div>
        </div>
      </div>

      {/* Companies by sector */}
      <div className="space-y-4 sm:space-y-6">
        {Object.entries(companiesBySector)
          .sort(([a], [b]) => a.localeCompare(b))
          .map(([sector, sectorCompanies]) => (
            <div key={sector}>
              <h2 className="text-base sm:text-lg font-semibold text-star-bright mb-2 sm:mb-3 flex items-center gap-2">
                <span>{sector}</span>
                <span className="text-xs sm:text-sm font-normal text-star-dim">({sectorCompanies.length})</span>
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                {sectorCompanies.map((company) => (
                  <CompanyCard
                    key={company.id}
                    company={company}
                    onResearch={(id) => researchMutation.mutate(id)}
                    activeRunId={activeResearch[company.id]}
                    progress={progressMap[company.id]}
                  />
                ))}
              </div>
            </div>
          ))}
      </div>

      {filteredCompanies.length === 0 && (
        <div className="text-center py-12">
          <p className="text-star-dim">No companies found</p>
          <button
            onClick={() => setShowAddModal(true)}
            className="mt-4 text-signal-blue hover:underline"
          >
            Add your first company
          </button>
        </div>
      )}

      {/* Add Company Modal */}
      <AddCompanyModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSubmit={(data) => createMutation.mutate(data)}
      />

      {/* Error toast for mutations */}
      <AnimatePresence>
        {(createMutation.error || researchMutation.error) && (
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 50 }}
            className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-4 sm:w-auto glass-card p-3 sm:p-4 rounded-lg border border-signal-red/30"
          >
            <p className="text-signal-red text-xs sm:text-sm">
              {(createMutation.error as Error)?.message || (researchMutation.error as Error)?.message}
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}
